import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Building2, Sprout, Swords, Share2, Link2, Twitter, MessageSquare, ImageDown, Loader2, Sparkles, CalendarDays, QrCode, Users } from "lucide-react";
import { api } from "@/lib/api";
import { CinematicVideo } from "@/components/CinematicVideo";

// Bandes-annonces cinématiques des prochains débats (id du débat -> URL vidéo).
// À compléter au fur et à mesure que les vidéos sont fournies (même principe que les conférences).
const DEBATE_TRAILERS = {
  "eval": "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/u7ny8rl7_gemini_generated_video_8a73dbfd.mp4",
  "programme": "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/lf96di1y_gemini_generated_video_9d33a825.mp4",
  "metier": "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/d94fmkv9_gemini_generated_video_f06597eb.mp4",
  "effort": "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/dif8av4z_deevid_video_32680550.mp4",
  "matieres": "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/tpdvwuto_gemini_generated_video_2ddc592b.mp4",
  "creativity-vs-conformity": "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/zh5atfv2_gemini_generated_video_51bbc1f9.mp4",
  "failure-as-tool": "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/5xygm6dn_gemini_generated_video_66fd5485.mp4",
  "teacher-vs-mentor": "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/qc0g45hp_gemini_generated_video_e440f23f.mp4",
  "boredom-in-learning": "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/stps1cqa_gemini_generated_video_b782df2b.mp4",
  "early-specialization": "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/zvc95ffd_gemini_generated_video_541684e4.mp4",
  "competition-vs-cooperation": "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/7xlem4uc_gemini_generated_video_3b1e825a.mp4",
  "memorization-vs-understanding": "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/f54635x0_gemini_generated_video_1a2d091f.mp4",
};

// Étiquettes de mois pour les N prochains mois (localisées).
function upcomingMonthLabels(n, lang) {
  const locale = { fr: "fr-FR", en: "en-US", es: "es-ES", nl: "nl-NL" }[lang] || "fr-FR";
  const now = new Date();
  const out = [];
  for (let k = 1; k <= n; k++) {
    const d = new Date(now.getFullYear(), now.getMonth() + k, 1);
    let label = d.toLocaleDateString(locale, { month: "long", year: "numeric" });
    out.push(label.charAt(0).toUpperCase() + label.slice(1));
  }
  return out;
}

function getVoterId() {
  let id = localStorage.getItem("coccon_voter_id");
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `v_${Date.now()}_${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("coccon_voter_id", id);
  }
  return id;
}

export function Arene() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const i18nDebates = t("arene.debates", { returnObjects: true }) || [];
  const [monthly, setMonthly] = useState([]);
  const [active, setActive] = useState(0);
  const [tally, setTally] = useState({});
  const [voting, setVoting] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const voterId = useMemo(() => getVoterId(), []);
  const [partCount, setPartCount] = useState(null);
  useEffect(() => {
    api.get("/stats/public").then(({ data }) => setPartCount(data?.debate_participation ?? null)).catch(() => {});
  }, []);
  const partLabel = { fr: "participations aux débats", en: "debate participations", es: "participaciones en debates", nl: "debatdeelnames" }[lang] || "participations aux débats";
  const monthlyLabel = { fr: "Débat du mois", en: "Debate of the month", es: "Debate del mes", nl: "Debat van de maand" }[lang] || "Débat du mois";
  const upcomingTitle = { fr: "Les prochains débats", en: "Upcoming debates", es: "Los próximos debates", nl: "Aankomende debatten" }[lang] || "Les prochains débats";
  const upcomingSubtitle = { fr: "Un nouveau duel d'idées chaque mois", en: "A new clash of ideas every month", es: "Un nuevo duelo de ideas cada mes", nl: "Elke maand een nieuw ideeënduel" }[lang] || "Un nouveau duel d'idées chaque mois";
  const monthLabels = useMemo(() => upcomingMonthLabels(Math.max(i18nDebates.length, 12), lang), [lang, i18nDebates.length]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debates = useMemo(() => [...monthly, ...i18nDebates], [monthly, lang, i18nDebates.length]);

  useEffect(() => {
    api.get(`/debates/arena?lang=${lang}`).then(({ data }) => setMonthly(data.items || [])).catch(() => {});
  }, [lang]);

  useEffect(() => {
    api.get(`/debates/tally?voter_id=${encodeURIComponent(voterId)}`)
      .then(({ data }) => {
        const map = {};
        (data.debates || []).forEach((d) => { map[d.debate_id] = d; });
        setTally(map);
      })
      .catch(() => {});
  }, [voterId]);

  // Deep-link: /?debat=<id> preselects & scrolls to the debate
  useEffect(() => {
    if (!debates.length) return;
    const wanted = new URLSearchParams(window.location.search).get("debat");
    if (!wanted) return;
    const idx = debates.findIndex((d) => d.id === wanted);
    if (idx >= 0) {
      setActive(idx);
      setTimeout(() => document.getElementById("debat-du-mois")?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
  }, [debates.length]); // eslint-disable-line

  const current = debates[active] || {};
  const stat = tally[current.id] || { ecole: 0, cocon: 0, total: 0, my_vote: null };
  const total = stat.total || 0;
  const ecolePct = total ? Math.round((stat.ecole / total) * 100) : 50;
  const coconPct = total ? 100 - ecolePct : 50;

  const vote = async (side) => {
    if (voting) return;
    setVoting(true);
    try {
      const { data } = await api.post("/debates/vote", { debate_id: current.id, voter_id: voterId, side });
      setTally((prev) => ({ ...prev, [current.id]: data }));
      toast.success(t("arene.voted"));
    } catch {
      toast.error(t("arene.voteError"));
    } finally {
      setVoting(false);
    }
  };

  if (!debates.length) return null;

  const shareUrl = `${window.location.origin}/?debat=${current.id}`;
  const shareText = t("arene.shareText", { question: current.question, ecole: ecolePct, cocon: coconPct });
  const enc = encodeURIComponent;
  const twitter = `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(shareUrl)}`;
  const whatsapp = `https://wa.me/?text=${enc(shareText + " " + shareUrl)}`;

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); } catch {}
    toast.success(t("arene.linkCopied"));
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "COCOON — L'Arène", text: shareText, url: shareUrl }); } catch {}
    } else { copyLink(); }
  };

  const shareCard = async () => {
    if (cardLoading) return;
    setCardLoading(true);
    try {
      const { data: blob } = await api.post("/debates/card", {
        debate_id: current.id,
        lang: (i18n.language || "fr").slice(0, 2),
        question: current.question,
        ecole_label: t("arene.ecoleLabel"),
        cocon_label: t("arene.cocoonLabel"),
        eyebrow: t("arene.title"),
        ecole_arg: current.ecole || "",
        cocon_arg: current.cocon || "",
        cta: t("arene.cardCta"),
      }, { responseType: "blob" });
      const file = new File([blob], `cocoon-arene-${current.id}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], text: shareText }); } catch {}
      } else {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u; a.download = file.name; document.body.appendChild(a); a.click();
        a.remove(); URL.revokeObjectURL(u);
        toast.success(t("arene.cardReady"));
      }
    } catch { toast.error(t("arene.cardError")); }
    finally { setCardLoading(false); }
  };

  return (
    <section id="arene" className="max-w-5xl mx-auto px-6 py-14 sm:py-24" data-testid="arene-section">
      <div className="text-center mb-10">
        <p className="text-primary/80 tracking-[0.25em] uppercase text-xs mb-5">{t("arene.eyebrow")}</p>
        <h2 className="font-display text-gradient-orange text-4xl sm:text-5xl tracking-tight inline-flex items-center gap-3 justify-center">
          <Swords size={34} className="text-primary/80" /> {t("arene.title")}
        </h2>
        {partCount != null && (
          <div className="flex justify-center mt-4">
            <span className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 border border-primary/25 text-sm text-foreground/70" data-testid="arene-participation-count">
              <Users size={14} className="text-primary/80" />
              <span className="font-display text-gradient-orange tabular-nums">{new Intl.NumberFormat(lang).format(partCount)}</span>
              {partLabel}
            </span>
          </div>
        )}
        <div className="mt-8 max-w-3xl mx-auto">
          <CinematicVideo fade sound src="https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/ltpabc7e_gemini_generated_video_bebf4756.mp4" testId="arene-cinematic-video" caption={t("arene.subtitle")} />
        </div>
      </div>

      {/* tabs 01–05 */}
      <div className="flex flex-wrap justify-center gap-2.5 mb-10" data-testid="arene-tabs">
        {debates.map((d, i) => (
          <button
            key={d.id}
            data-testid={`arene-tab-${d.id}`}
            onClick={() => setActive(i)}
            className={`px-4 py-2 rounded-full text-sm transition-colors border inline-flex items-center gap-1.5 ${
              active === i
                ? "bg-primary/15 text-primary border-primary/40"
                : "text-foreground/50 border-white/10 hover:text-foreground/80 hover:border-white/20"
            }`}
          >
            {d.monthly ? <Sparkles size={13} /> : <span className="tabular-nums font-display">{String(i + 1).padStart(2, "0")}</span>}
          </button>
        ))}
      </div>

      <div id="debat-du-mois" aria-hidden style={{ scrollMarginTop: "128px" }} />
      <AnimatePresence mode="wait">
        <motion.div
          key={current.id}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.4 }}
          className="stats-wow rounded-3xl p-6 sm:p-10"
          data-testid="arene-card"
        >
          <p className="text-center text-xs uppercase tracking-[0.25em] text-foreground/40 mb-3">
            {current.monthly ? (
              <span className="inline-flex items-center gap-1.5 text-primary" data-testid="arene-monthly-badge">
                <Sparkles size={13} /> {monthlyLabel}
              </span>
            ) : (
              <>{t("arene.debateLabel")} {String(active + 1).padStart(2, "0")}</>
            )}
          </p>
          <h3 className="text-center font-display text-gradient-orange text-2xl sm:text-3xl leading-tight mb-10" data-testid="arene-question">
            {current.question}
          </h3>

          {/* duel */}
          <div className="grid md:grid-cols-2 gap-5 mb-10">
            <div className="rounded-2xl p-6 bg-slate-400/5 border border-slate-300/15" data-testid="arene-ecole-col">
              <div className="flex items-center gap-2 mb-3 text-slate-300/80">
                <Building2 size={18} />
                <span className="font-display text-lg">{t("arene.ecoleLabel")}</span>
              </div>
              <p className="text-foreground/65 leading-relaxed text-sm italic">« {current.ecole} »</p>
            </div>
            <div className="rounded-2xl p-6 bg-primary/[0.06] border border-primary/25" data-testid="arene-cocon-col">
              <div className="flex items-center gap-2 mb-3 text-primary">
                <Sprout size={18} />
                <span className="font-display text-lg">{t("arene.cocoonLabel")}</span>
              </div>
              <p className="text-foreground/75 leading-relaxed text-sm italic">« {current.cocon} »</p>
            </div>
          </div>

          {/* gauge */}
          <div className="mb-6" data-testid="arene-gauge">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-300/80 tabular-nums" data-testid="arene-ecole-pct">{ecolePct}% · {t("arene.ecoleLabel")}</span>
              <span className="text-primary tabular-nums" data-testid="arene-cocon-pct">{t("arene.cocoonLabel")} · {coconPct}%</span>
            </div>
            <div className="h-4 rounded-full overflow-hidden flex bg-background/40 border border-white/10">
              <motion.div
                className="h-full bg-slate-400/60"
                animate={{ width: `${ecolePct}%` }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              />
              <motion.div
                className="h-full bg-gradient-to-r from-primary/80 to-primary flex-1"
                animate={{ width: `${coconPct}%` }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              />
            </div>
            <p className="text-center text-xs text-foreground/45 mt-3" data-testid="arene-total">
              {total > 0 ? t("arene.totalVotes", { count: total }) : t("arene.noVotes")}
            </p>
          </div>

          {/* vote buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              data-testid="arene-vote-ecole"
              onClick={() => vote("ecole")}
              disabled={voting}
              className={`px-6 py-3 rounded-full text-sm font-semibold transition-all border disabled:opacity-50 ${
                stat.my_vote === "ecole"
                  ? "bg-slate-300/20 text-slate-100 border-slate-300/50"
                  : "text-slate-300/80 border-slate-300/25 hover:border-slate-300/50 hover:bg-slate-300/10"
              }`}
            >
              {t("arene.voteEcole")}
            </button>
            <button
              data-testid="arene-vote-cocon"
              onClick={() => vote("cocon")}
              disabled={voting}
              className={`px-6 py-3 rounded-full text-sm font-semibold transition-all ${
                stat.my_vote === "cocon" ? "btn-frost-orange ring-2 ring-primary/50" : "btn-frost-orange"
              }`}
            >
              {t("arene.voteCocon")}
            </button>
          </div>
          {stat.my_vote && (
            <p className="text-center text-xs text-foreground/40 mt-4" data-testid="arene-change-hint">{t("arene.changeHint")}</p>
          )}

          <div className="mt-8 pt-6 border-t border-white/10">
            <p className="text-center uppercase tracking-[0.22em] text-[11px] text-foreground/40 mb-4 flex items-center justify-center gap-2">
              <Share2 size={13} /> {t("arene.share")}
            </p>
            <div className="flex flex-wrap gap-2.5 justify-center" data-testid="arene-share">
              <button onClick={shareCard} disabled={cardLoading} data-testid="arene-share-card" className="btn-frost-orange rounded-full px-5 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
                {cardLoading ? <Loader2 size={14} className="animate-spin" /> : <ImageDown size={14} />} {t("arene.card")}
              </button>
              <button onClick={copyLink} data-testid="arene-share-copy" className="glass rounded-full px-4 py-2 text-sm text-foreground/70 hover:text-primary transition-colors inline-flex items-center gap-2">
                <Link2 size={14} /> {t("arene.copyLink")}
              </button>
              <a href={twitter} target="_blank" rel="noopener noreferrer" data-testid="arene-share-twitter" className="glass rounded-full px-4 py-2 text-sm text-foreground/70 hover:text-primary transition-colors inline-flex items-center gap-2">
                <Twitter size={14} /> X
              </a>
              <a href={whatsapp} target="_blank" rel="noopener noreferrer" data-testid="arene-share-whatsapp" className="glass rounded-full px-4 py-2 text-sm text-foreground/70 hover:text-primary transition-colors inline-flex items-center gap-2">
                <MessageSquare size={14} /> WhatsApp
              </a>
              <button onClick={nativeShare} data-testid="arene-share-native" className="glass rounded-full px-4 py-2 text-sm text-foreground/70 hover:text-primary transition-colors inline-flex items-center gap-2 sm:hidden">
                <Share2 size={14} /> {t("arene.share")}
              </button>
              <Link to={`/invitation?ev=debat&id=${encodeURIComponent(current.id)}`} data-testid="arene-share-eventqr" className="glass rounded-full px-4 py-2 text-sm text-foreground/70 hover:text-primary transition-colors inline-flex items-center gap-2">
                <QrCode size={14} /> {t("arene.eventQr", "QR d'événement")}
              </Link>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Les prochains débats — un par mois (comme les conférences) */}
      {i18nDebates.length > 0 && (
        <div className="mt-20" data-testid="arene-upcoming">
          <div className="text-center mb-9">
            <p className="text-primary/70 tracking-[0.22em] uppercase text-xs mb-3">{upcomingSubtitle}</p>
            <h3 className="font-display text-gradient-orange text-3xl sm:text-4xl tracking-tight">{upcomingTitle}</h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
            {i18nDebates.map((d, i) => {
              const trailer = DEBATE_TRAILERS[d.id];
              return (
                <motion.div
                  key={d.id}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
                  className="glass frame-pulse rounded-2xl border border-primary/12 p-5 sm:p-6 text-left"
                  data-testid={`arene-upcoming-${d.id}`}
                >
                  <div className="flex items-center gap-2 text-primary/70 text-[11px] uppercase tracking-[0.2em] mb-2.5">
                    <CalendarDays size={13} /> {monthLabels[i]}
                  </div>
                  <h4 className="font-display text-lg sm:text-xl text-foreground/90 leading-snug mb-4">{d.question}</h4>
                  {trailer && (
                    <div className="rounded-xl overflow-hidden border border-primary/15" data-testid={`arene-upcoming-${d.id}-trailer`}>
                      <CinematicVideo fade sound soundMask src={trailer} testId={`arene-upcoming-${d.id}-video`} caption={d.question} />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
